/* eslint-disable react/prop-types */
import { Box } from "flicket-ui";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useOrganization } from "~hooks";
import { useCreateGAOrder } from "../../nonseated.hooks";
import { useNonSeatedReservationStore } from "../../store/non-seated-reservation-store";
import { animation } from "./ExperienceSalesFooter.animation";
import { IExperienceSalesLayout } from "./types";
import { useTicketExchangeData, useFooterActions } from "./hooks";
import { getButtonText, reservationStickyFooter } from "./utils";
import { Cart } from "~features/checkout/cart";
import {
  ErrorModal,
  FooterActionBar,
  PriceInfo,
  TicketExchangeSection,
} from "./components";

const MotionBox = motion(Box);

export const ExperienceSalesFooter = ({
  id,
  releaseId,
  membershipId,
  children,
}: IExperienceSalesLayout) => {
  const { hasFeature, organization } = useOrganization();

  // State to track the exchange-adjusted price
  const [exchangeAdjustedPrice, setExchangeAdjustedPrice] = useState(0);

  // Get ticket exchange data
  const {
    ownedTicketData,
    hasOwnedTickets: isExchangeFlow,
    ticketsLoading,
    hasTickets,
    ownedTicketsExchangeValue,
  } = useTicketExchangeData(organization?.id);

  // Get state from order creation hook
  const { handleGAOrderCreation } = useCreateGAOrder({
    releaseId,
    membershipId,
    eventId: id,
  });

  const selectedTickets = useNonSeatedReservationStore(
    (state) => state.selectedTicketTypes[id] ?? []
  );

  const cart = useNonSeatedReservationStore(
    (state) => state.totals[id] ?? { total: 0, multibuyId: undefined }
  );

  // Set the ticket exchange value on the cart when data is available
  useEffect(() => {
    if (isExchangeFlow && cart instanceof Cart) {
      if (cart.ticketExchangeValue === 0) {
        cart.addFee(-(ownedTicketsExchangeValue ?? 0));
      }
      // Set the exchange value directly on the cart
      cart.setTicketExchangeValue(ownedTicketsExchangeValue);

      // Simply use the cart's total which already has the exchange value and fees calculated correctly
      setExchangeAdjustedPrice(cart.total);
    } else if (!isExchangeFlow && cart) {
      // For regular purchases, use the cart total
      setExchangeAdjustedPrice(cart.total);
    }
  }, [isExchangeFlow, ownedTicketsExchangeValue, cart]);

  // Update the exchange-adjusted price whenever the cart total changes
  useEffect(() => {
    // For all purchase types, use the cart total which already has the correct calculations
    setExchangeAdjustedPrice(cart.total);
  }, [cart.total, isExchangeFlow, ownedTicketsExchangeValue, cart]);

  // Get the global submitting state from the store
  const isStoreSubmitting = useNonSeatedReservationStore(
    (state) => state.isSubmitting
  );

  // Set up footer actions
  const {
    submitting,
    alertError,
    setAlertError,
    resetState,
    handlePrimaryAction,
  } = useFooterActions({
    handleGAOrderCreation,
    selectedTickets,
    cart,
    isExchangeFlow,
    ownedTicketsExchangeValue,
    ownedTicketData, // Pass the owned ticket data to the hook
  });

  const hasSelection = selectedTickets.length > 0;
  const showFees = hasFeature("showHiddenFees");

  // Only show footer when either tickets are selected or in exchange flow
  const showFooter =
    !hasFeature("LegacyGATicketSelection") && (hasSelection || isExchangeFlow);

  // Get button text based on current state
  const buttonText = getButtonText(isExchangeFlow, hasSelection);

  return (
    <>
      <Box width="100%">
        {children}
        <AnimatePresence>
          {showFooter && (
            <MotionBox
              {...animation}
              position={
                reservationStickyFooter(organization?.id) ? "fixed" : undefined
              }
              zIndex="sticky"
              bottom={0}
              right={0}
              left={0}
              width={1}
              bg="white"
              boxShadow="card"
            >
              <Box
                flex={1}
                px={2}
                py={3}
                maxW="960px"
                mx={"auto" as any}
                minH={80}
              >
                <TicketExchangeSection
                  isExchangeFlow={isExchangeFlow}
                  ticketsLoading={ticketsLoading}
                  hasTickets={hasTickets}
                  ownedTicketData={ownedTicketData}
                  ownedTicketsExchangeValue={ownedTicketsExchangeValue}
                />

                <FooterActionBar
                  priceInfo={
                    <PriceInfo
                      isExchangeFlow={isExchangeFlow}
                      ticketsLoading={ticketsLoading}
                      hasSelection={hasSelection}
                      cart={cart}
                      ownedTicketsExchangeValue={ownedTicketsExchangeValue}
                      showFees={showFees}
                      exchangeAdjustedPrice={exchangeAdjustedPrice}
                      hasFeature={hasFeature}
                    />
                  }
                  buttonText={buttonText}
                  onAction={handlePrimaryAction}
                  isLoading={submitting || isStoreSubmitting}
                  isDisabled={
                    !hasSelection ||
                    (isExchangeFlow && ticketsLoading) ||
                    isStoreSubmitting
                  }
                />
              </Box>
            </MotionBox>
          )}
        </AnimatePresence>
      </Box>

      <ErrorModal error={alertError} onClose={() => setAlertError(undefined)} />
    </>
  );
};
